
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { toast } from '@/components/ui/use-toast';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Check, Plus, PlusCircle, UserMinus, UserPlus, X } from 'lucide-react';
import { useUserManagement } from '@/hooks/useUserManagement';

const TeamManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdminOrHigher, canManageTeam } = useRolePermissions();
  const { createUser, deleteUser, toggleAdminStatus, toggleUserActive, changeUserRole } = useUserManagement();
  
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!canManageTeam) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to manage team members',
        variant: 'destructive'
      });
      navigate('/dashboard');
      return;
    }

    fetchTeamMembers();
  }, [canManageTeam, navigate]);

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from('user_roles')
        .select('user_id, role, is_active, created_at, subscription_status, plan_tier')
        .eq('created_by', user.id);

      if (error) {
        throw error;
      }

      if (data) {
        // Now we need to get emails for these users
        try {
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/user-emails`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const emailsData = await response.json();
          
          // Add emails to the team members
          const membersWithEmails = data.map(member => ({
            ...member,
            email: emailsData.data[member.user_id] || `user-${member.user_id.substring(0, 8)}@example.com`
          }));

          setTeamMembers(membersWithEmails);
        } catch (emailError) {
          console.error('Error fetching user emails:', emailError);
          
          // Fall back to using user IDs instead of emails
          const membersWithIds = data.map(member => ({
            ...member,
            email: `user-${member.user_id.substring(0, 8)}@example.com`
          }));

          setTeamMembers(membersWithIds);
        }
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!user) {
      toast({
        title: 'Error',
        description: 'You must be logged in to invite team members',
        variant: 'destructive'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Create the new user with the selected role
      const newUser = await createUser(newUserEmail, newUserPassword, newUserRole);

      if (newUser) {
        // Set current user as the creator
        const { error: updateError } = await supabase
          .from('user_roles')
          .update({ created_by: user.id })
          .eq('user_id', newUser.id);

        if (updateError) {
          throw updateError;
        }

        toast({
          title: 'Success',
          description: 'Team member invited successfully'
        });

        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserRole('viewer');
        setIsDialogOpen(false);
        fetchTeamMembers();
      }
    } catch (error) {
      console.error('Error inviting member:', error);
      toast({
        title: 'Error',
        description: 'Failed to invite team member',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await deleteUser(memberId);
      fetchTeamMembers();
      
      toast({
        title: 'Success',
        description: 'Team member removed successfully'
      });
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove team member',
        variant: 'destructive'
      });
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      await changeUserRole(memberId, newRole);
      fetchTeamMembers();
      
      toast({
        title: 'Success',
        description: 'Role updated successfully'
      });
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        title: 'Error',
        description: 'Failed to update role',
        variant: 'destructive'
      });
    }
  };

  const handleToggleActive = async (memberId: string, currentStatus: boolean) => {
    try {
      await toggleUserActive(memberId, currentStatus);
      fetchTeamMembers();
      
      toast({
        title: 'Success',
        description: `User ${currentStatus ? 'deactivated' : 'activated'} successfully`
      });
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update user status',
        variant: 'destructive'
      });
    }
  };

  const handlePromoteToAdmin = async (memberId: string, isCurrentlyAdmin: boolean) => {
    try {
      await toggleAdminStatus(memberId, !isCurrentlyAdmin);
      fetchTeamMembers();
      
      toast({
        title: 'Success',
        description: `User ${isCurrentlyAdmin ? 'demoted from admin' : 'promoted to admin'} successfully`
      });
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update admin status',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="container mx-auto py-6">
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Team Management</CardTitle>
          <CardDescription>Invite and manage team members to collaborate on your dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              <div className="mb-6 flex justify-between items-center">
                <h3 className="text-lg font-medium">Team Members</h3>
                <Button onClick={() => setIsDialogOpen(true)} className="flex items-center gap-2">
                  <PlusCircle size={16} />
                  Invite Member
                </Button>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Added On</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No team members found. Invite someone to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    teamMembers.map((member) => (
                      <TableRow key={member.user_id}>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <Select
                            value={member.role}
                            onValueChange={(value) => handleRoleChange(member.user_id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={member.is_active}
                              onCheckedChange={() => handleToggleActive(member.user_id, member.is_active)}
                            />
                            <span>
                              {member.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(member.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePromoteToAdmin(member.user_id, member.role === 'admin')}
                            >
                              {member.role === 'admin' ? (
                                <>
                                  <UserMinus className="w-4 h-4 mr-1" />
                                  Demote
                                </>
                              ) : (
                                <>
                                  <UserPlus className="w-4 h-4 mr-1" />
                                  Promote
                                </>
                              )}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleRemoveMember(member.user_id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
        <CardFooter className="border-t pt-4 flex justify-between">
          <div>
            <p className="text-sm text-gray-500">
              Team members can access your dashboard based on their role.
            </p>
          </div>
          <Button variant="outline" onClick={fetchTeamMembers}>Refresh</Button>
        </CardFooter>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Add a new team member to collaborate on your dashboard.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteMember}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="member@example.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Temporary Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a temporary password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500">
                  The user will be able to change this after login.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={newUserRole} onValueChange={setNewUserRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (Full Access)</SelectItem>
                    <SelectItem value="member">Member (Edit Access)</SelectItem>
                    <SelectItem value="viewer">Viewer (View Only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full" />
                    Inviting...
                  </>
                ) : (
                  <>Invite Member</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
